import { useState, type ReactNode } from 'react'
import { Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

/**
 * Unsaved-changes guard for dialog-hosted forms. Wrap the dialog's
 * onOpenChange: when the user closes a dirty form via Esc, the overlay click,
 * or the Cancel button, a confirmation opens instead of silently discarding
 * their input.
 *
 *   const { guardOpenChange, discardGuard } = useDirtyClose(open, onOpenChange, isDirty)
 *   <Dialog open={open} onOpenChange={guardOpenChange}>…</Dialog>
 *   {discardGuard}
 */
export function useDirtyClose(
  open: boolean,
  onOpenChange: (open: boolean) => void,
  isDirty: boolean,
): {
  guardOpenChange: (open: boolean) => void
  discardGuard: ReactNode
} {
  const [confirmOpen, setConfirmOpen] = useState(false)

  // Re-created each render, so it always sees the latest dirty state; only
  // guard while the owning dialog is open — a parent-driven close (e.g. after
  // a successful save) must never prompt.
  const guardOpenChange = (next: boolean) => {
    if (!next && open && isDirty) {
      setConfirmOpen(true)
      return
    }
    onOpenChange(next)
  }

  const discard = () => {
    setConfirmOpen(false)
    onOpenChange(false)
  }

  const discardGuard = (
    <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Discard your changes?</DialogTitle>
          <DialogDescription>
            This form has unsaved changes. Closing it discards them permanently.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setConfirmOpen(false)}>
            Keep Editing
          </Button>
          <Button variant="destructive" onClick={discard}>
            <Trash2 className="mr-2 h-4 w-4" />
            Discard Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )

  return { guardOpenChange, discardGuard }
}
